// --- React
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
// --- Config & Utils
import * as columnDefs from "../../../../../components/AGGrid/config/columns-def";
// --- Hooks
import {
  useTPRExceptionsData,
  beginTprExceptionsFetch,
  isLatestTprExceptionsFetch,
} from "../../../hooks/useTPRExceptionsData";
import { buildTprExceptionsPageUpdate } from "../../../utils/paginationUtils";

/**
 * Custom hook for TPR Exceptions grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the TPR Exceptions grid
 * - Fetches sorted data from the API
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Optional array of column handler functions
 * @returns {object} { setSort, columns }
 */
export function useTPRExceptionsGridUtils({ columnHandlers = [] } = {}) {
  const {
    fetchTPRExceptions,
    updateTPRExceptions,
    tprExceptionsRange,
    itemsPerPage,
  } = useTPRExceptionsData();

  const { totalItems } = useSelector((s) => s.tprExceptions);

  const [sortedColId, setSortedColId] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sorting: update state, fetch sorted data, update TPR Exceptions
  const setSort = useCallback(
    async (colId, direction) => {
      setSortedColId(colId);
      setSortDirection(direction);

      const customSort = [colId, direction.toUpperCase()];
      const fetchId = beginTprExceptionsFetch();
      const data = await fetchTPRExceptions(
        tprExceptionsRange,
        null,
        customSort,
      );

      if (data && isLatestTprExceptionsFetch(fetchId)) {
        updateTPRExceptions({
          ...buildTprExceptionsPageUpdate(data, {
            itemsPerPage,
            range: tprExceptionsRange,
            previousTotalItems: totalItems,
            preserveTotal: false,
          }),
          tprExceptionsSort: customSort,
        });
      }
    },
    [
      fetchTPRExceptions,
      updateTPRExceptions,
      tprExceptionsRange,
      itemsPerPage,
      totalItems,
    ],
  );

  // Prepare column definitions with sorting controls
  const baseColumns = columnDefs["tprExceptions"](...columnHandlers)["default"];

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { setSort, columns };
}
